import React, { useState, useEffect } from 'react';
import { Container, Card, Row, Col, Button, Badge } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { subscriptionPlansApi, SubscriptionTransaction } from './SubscriptionPlansApi.tsx';
import { useApiLoader } from "../../../hooks/useApiLoader.tsx";
import IconTransaction from "../../../images/orders/icon_trasaction.png";
import IconCalendarOutlook from "../../../images/orders/icon_calendar-outlook.png";
import IconCurrency from "../../../images/orders/icon_curr_icon.png";
import IconGiftType from "../../../images/orders/icon_gift-type.png";
import IconCard from "../../../images/orders/icon_card.png";
import IconCurrencyOutline from "../../../images/orders/icon_curr-outline_icon.png";
import IconProfile from "../../../images/profile-icon.svg";
import IconMail from "../../../images/mail-icon.svg";

interface SubscriptionDetailsProps {}

const SubscriptionDetails: React.FC<SubscriptionDetailsProps> = () => {
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const navigate = useNavigate();
  const { withLoader } = useApiLoader();

  const [subscription, setSubscription] = useState<SubscriptionTransaction | null>(null);
  const [loading, setLoading] = useState(true);

  const formatDateTime = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';

      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
      }) + ' ' + date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const fetchSubscriptionDetails = async () => {
    if (!subscriptionId) {
      toast.error('Subscription ID is required');
      navigate('/subscription-plans');
      return;
    }

    try {
      setLoading(true);
      const response = await withLoader(
        () => subscriptionPlansApi.getSubscriptionById(subscriptionId),
        'Loading subscription details...'
      );

      if (response.status === 'Success' && response.data) {
        setSubscription(response.data);
      } else {
        toast.error(response.message || 'Failed to fetch subscription details');
        navigate('/subscription-plans');
      }
    } catch (error: any) {
      console.error('Error fetching subscription details:', error);
      toast.error('Failed to fetch subscription details');
      navigate('/subscription-plans');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    let badgeClass = '';
    let displayText = status;

    switch (status?.toLowerCase()) {
      case 'active':
        badgeClass = 'success';
        displayText = 'Active';
        break;
      case 'inactive':
      case 'cancelled':
        badgeClass = 'danger';
        displayText = 'Inactive';
        break;
      case 'pending':
        badgeClass = 'warning';
        displayText = 'Pending';
        break;
      case 'expired':
        badgeClass = 'secondary';
        displayText = 'Expired';
        break;
      default:
        badgeClass = 'secondary';
        displayText = status || 'Unknown';
    }
    return <Badge bg={badgeClass}>{displayText}</Badge>;
  };

  useEffect(() => {
    fetchSubscriptionDetails();
  }, [subscriptionId]);

  if (loading) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <div className="spinner-border" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3">Loading subscription details...</p>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  if (!loading && !subscription) {
    return (
      <section className='main-body'>
        <Container fluid className="p-0">
          <Card.Body>
            <div className="text-center py-5">
              <p className="text-muted">Subscription not found</p>
              <Button variant="primary" onClick={() => navigate('/subscription-plans')}>
                Back to Subscription Plans
              </Button>
            </div>
          </Card.Body>
        </Container>
      </section>
    );
  }

  return (
    <section className='main-body'>
      <Container fluid className="p-0">
        <Card.Body>
          {/* Breadcrumb */}
          <div className="d-flex align-items-center">
            <Button
              variant="link"
              className="text-decoration-none p-0 fw-bold d-flex align-items-center gap-2 btn btn-link me-2"
              style={{color: '#3957a0'}}
              onClick={() => navigate('/subscription-plans')}
            >
              ← Back
            </Button>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <span className="text-muted">Subscription Plans</span>
                </li>
                <li className="breadcrumb-item active">
                  {subscription?.subscriptionId || subscription?.id || subscriptionId}
                </li>
              </ol>
            </nav>
          </div>

          {/* Header Section with Transaction ID and User Info */}
          <div className="details-block">
            <div className="details-block-in">
              <div className="row align-items-center ">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconTransaction} alt="Plan Type" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Transaction ID</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.transactionId || subscription?.transactionIdLast5 || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                 
                </div>
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconProfile} alt="Plan Type" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>User Name</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.customerName || subscription?.userName || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                 
                </div>
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconMail} alt="Plan Type" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Email</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.customerEmail || subscription?.email || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                </div>
              </div>

              {/* First Row */}
              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconGiftType} alt="Plan Type" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Plan Type</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {(subscription?.billingCycle || subscription?.subscriptionType) === 'monthly' ? 'Monthly' : 'Yearly'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconCalendarOutlook} alt="Start Date" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Start Date</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.startDate ? formatDateTime(subscription.startDate) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconCalendarOutlook} alt="End Date" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>End Date</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.endDate ? formatDateTime(subscription.endDate) : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Second Row */}
              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconGiftType} alt="Subscription Level" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Subscription Level</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.subscriptionPlanName || subscription?.planType || 'Basic'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconCard} alt="Promo Code" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Promo Code</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.coupon || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconCurrency} alt="Amount" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Amount</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.amount
                            ? formatCurrency(subscription.amount)
                            : subscription?.amountPaid
                              ? formatCurrency(subscription.amountPaid)
                              : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Third Row */}
              <div className="row">
                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconCurrencyOutline} alt="Discount" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Discount</label>
                        <p className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {subscription?.discount ? `$${subscription.discount}` : '$0'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="gift-info-item mb-3">
                    <div className="d-flex align-items-start mb-2">
                      <div className="gift-icon me-2 my-auto">
                        <img src={IconTransaction} alt="Status" width="32" />
                      </div>
                      <div>
                        <label className="form-label mb-1 text-muted" style={{fontSize:'12px'}}>Status</label>
                        <div className="fw-bold mb-0" style={{fontSize:'16px'}}>
                          {getStatusBadge(subscription?.status || 'N/A')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card.Body>
      </Container>
    </section>
  );
};

export default SubscriptionDetails;